import { MediaMatcher } from '@angular/cdk/layout';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnInit,
  ViewContainerRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  OnDeletedFromOverlay,
  OverlayService,
} from 'src/app/shared/Services/overlay-service/overlay.service';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'side-panel',
  templateUrl: './side-panel.component.html',
  styleUrls: ['./side-panel.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidePanelComponent
  implements AfterViewInit, OnInit, OnDeletedFromOverlay
{
  private media = inject(MediaMatcher);
  private overlayService = inject(OverlayService);

  readonly topOffset = input<number>(undefined);

  statusBarQuery: MediaQueryList;
  maxWidth = 900;
  maxWidthEvent = new BehaviorSubject<boolean>(false);

  private readonly _contentSet = signal<boolean | undefined>(undefined);
  set contentSet(value: boolean | undefined) {
    this._contentSet.set(value);
  }
  get contentSet(): boolean | undefined {
    return this._contentSet();
  }

  @HostBinding('style.top.px') get yOffset(): number {
    return this.isMobile()
      ? this._topOffsetMobile()
      : this._yOffset() + this.topOffset();
  }

  set yOffset(px: number | undefined) {
    this._yOffset.set(px);
  }

  set topOffsetMobile(px: number | undefined) {
    this._topOffsetMobile.set(px);
  }

  private readonly _yOffset = signal(0);
  private readonly _topOffsetMobile = signal(0);

  readonly container = viewChild(`container`, { read: ViewContainerRef });

  private readonly _mobileView = signal<boolean>(
    window.innerWidth <= this.maxWidth,
  );
  readonly mobileView = this._mobileView.asReadonly();
  readonly mobileView$ = toObservable(this.mobileView);
  readonly isMobile = computed(() => this._mobileView());

  readonly visibleEvent = output<boolean>();
  readonly mobileViewEvent = output<boolean>();

  private visibilityInitialized = false;

  constructor() {
    // Re-emit visibility whenever the panel content is set/cleared. Replaces the
    // previous `setTimeout(() => this.updateVisibility())` deferral in the
    // contentSet setter; the effect runs after the change-detection pass, so the
    // emit stays deferred relative to imperative `contentSet` writes.
    //
    // Only the very first run (the initial `undefined`) is skipped, matching the
    // pre-zoneless behaviour where visibility never emitted on init. Every later
    // change — INCLUDING the clear-to-`undefined` performed on a page's destroy —
    // must emit so the host hides the panel. A previous `=== undefined` guard
    // swallowed that clear, leaving a blank panel visible on the next page
    // because the host's inline `display:block` overrides its `[hidden]` binding.
    effect(() => {
      const contentSet = this._contentSet();
      if (!this.visibilityInitialized) {
        this.visibilityInitialized = true;
        return;
      }
      this.visibleEvent.emit(!!contentSet);
    });
  }

  ngOnInit() {
    this.overlayService.registerContainer(`side-panel`, this.container());
    this.overlayService.registerComponent(`side-panel`, this);
  }

  ngAfterViewInit(): void {
    this.statusBarQuery = this.media.matchMedia(
      `(max-width: ${this.maxWidth}px)`,
    );
    // Safari fix
    if (this.statusBarQuery.addEventListener) {
      this.statusBarQuery.addEventListener(`change`, this.handleQuery);
    } else if (this.statusBarQuery.addListener) {
      this.statusBarQuery.addListener((event) => {
        if (event.type === `change`) this.handleQuery(event);
      });
    }

    this.dispatchStatusBarEvent();
  }

  handleQuery = ({ matches }: { matches: boolean; type: string }): void => {
    this.mobileViewEvent.emit(matches);
    this._mobileView.set(matches);
    this.maxWidthEvent.next(matches);

    if (this.contentSet === undefined) return undefined;
    this.contentSet = matches ? false : true;
  };

  dispatchStatusBarEvent(): void {
    this.handleQuery({
      type: `change`,
      matches: window.innerWidth <= this.maxWidth,
    });
  }

  tryHidePanel(): void {
    this.contentSet = this.isMobile() ? false : true;
  }

  onDeletedFromOverlay() {
    this.yOffset = 0;
    this._contentSet.set(undefined);
  }
}
